import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { AvatarSize } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IExercise } from '../../../types/interfaces/exercise';
import AppExerciseAvatar from '../../designe/app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';

const useStyles = makeStyles<Theme>()((theme) => ({
  linkWrapper: {
    ...theme.mixins.appLink,
  },

  label: {
    ...theme.mixins.appLabel,
  },
  exerciseTitleBlock: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
  exerciseTitle: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
}));

export default function ClickableExercise({
  exercise,
  withLabel = true,
  progressLabel,
  fromTrainingId,
  fromProgramId,
}: {
  exercise: IExercise;
  withLabel?: boolean;
  progressLabel?: string;
  fromTrainingId?: string;
  fromProgramId?: string;
}) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();
  const { t } = useTranslation();

  const onExerciseClick = useCallback(
    () =>
      exercise?.id &&
      navigate(
        Paths.exercises.toSummaryId(exercise.id, {
          fromTrainingId,
          fromProgramId,
        }),
      ),
    [exercise?.id, fromProgramId, fromTrainingId, navigate],
  );

  if (!exercise?.id) {
    return null;
  }

  return (
    <Box minWidth={0} width="100%">
      {withLabel && (
        <Typography variant="label-text-md" className={classes.label}>
          {t('common.exercise')}
        </Typography>
      )}
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1}
        marginTop={0.5}
        minWidth={0}
        className={classes.linkWrapper}
        onClick={() => onExerciseClick()}>
        <AppExerciseAvatar exercise={exercise} size={AvatarSize.s} />
        <Box className={classes.exerciseTitleBlock}>
          <Typography variant="text-lg" className={classes.exerciseTitle}>
            {exercise.title}
          </Typography>
          {!!progressLabel && (
            <Typography variant="label-text-md">
              {progressLabel} {t('common.sets').toLowerCase()}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
